import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import aviso, { textoDoErro } from '../lib/aviso';
import { LuMessageSquare, LuPencil, LuTrash2, LuPlus } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import ConfirmationModal from '../components/ConfirmationModal';
import AssistenteService, {
    avisarConversasMudaram,
    type ConversaAssistente,
} from '../services/AssistenteService';
import { quando } from '../utils/quando';
import './AssistenteLayout.css';

/**
 * Todas as conversas com o assistente — o destino do "Ver todas" da Home e da
 * sidebar. Mesma coluna de 560px das outras duas telas.
 */
const AssistenteConversasPage = () => {
    const navigate = useNavigate();

    const [conversas, setConversas] = useState<ConversaAssistente[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [renomeando, setRenomeando] = useState<number | null>(null);
    const [rascunho, setRascunho] = useState('');
    const [aExcluir, setAExcluir] = useState<ConversaAssistente | null>(null);
    const [excluindo, setExcluindo] = useState(false);

    useEffect(() => {
        let vivo = true;
        AssistenteService.listarConversas(100)
            .then(lista => { if (vivo) setConversas(lista); })
            .catch(() => { if (vivo) aviso.erro('Não foi possível carregar as conversas.'); })
            .finally(() => { if (vivo) setCarregando(false); });
        return () => { vivo = false; };
    }, []);

    const salvarTitulo = async (conversa: ConversaAssistente) => {
        const titulo = rascunho.trim();
        if (!titulo || titulo === conversa.titulo) { setRenomeando(null); return; }

        try {
            const atualizada = await AssistenteService.renomear(conversa.conversaId, titulo);
            setConversas(lista => lista.map(c => (c.conversaId === atualizada.conversaId ? atualizada : c)));
            avisarConversasMudaram();
        } catch (erro) {
            aviso.erro(textoDoErro(erro, 'Não foi possível renomear.'));
        } finally {
            setRenomeando(null);
        }
    };

    const excluir = async () => {
        if (!aExcluir) return;
        setExcluindo(true);
        try {
            await AssistenteService.remover(aExcluir.conversaId);
            setConversas(lista => lista.filter(c => c.conversaId !== aExcluir.conversaId));
            avisarConversasMudaram();
        } catch (erro) {
            aviso.erro(textoDoErro(erro, 'Não foi possível remover.'));
        } finally {
            setExcluindo(false);
            setAExcluir(null);
        }
    };

    return (
        <div className="page-shell as-screen">
            <PageHeader
                centered
                title="Conversas"
                subtitle="Suas conversas com o assistente. Ninguém mais tem acesso a elas."
                actions={
                    <button type="button" className="as-acao" onClick={() => navigate('/home')}>
                        <LuPlus size={14} /> Nova conversa
                    </button>
                }
            />

            <div className="page-body">
                <div className="as-coluna">
                    {carregando && <p className="as-vazio">Carregando…</p>}

                    {!carregando && conversas.length === 0 && (
                        <p className="as-vazio">
                            Você ainda não conversou com o assistente. Comece pela Home.
                        </p>
                    )}

                    {conversas.length > 0 && (
                        <div className="as-lista">
                            {conversas.map(c => (
                                <div key={c.conversaId} className="as-li">
                                    {renomeando === c.conversaId ? (
                                        <input
                                            className="as-titulo-input"
                                            value={rascunho}
                                            maxLength={60}
                                            autoFocus
                                            onChange={e => setRascunho(e.target.value)}
                                            onBlur={() => salvarTitulo(c)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') salvarTitulo(c);
                                                if (e.key === 'Escape') setRenomeando(null);
                                            }}
                                        />
                                    ) : (
                                        <button
                                            type="button"
                                            className="as-li-abrir"
                                            onClick={() => navigate(`/assistente/${c.conversaId}`)}
                                        >
                                            <LuMessageSquare size={16} />
                                            <span className="as-li-t">{c.titulo}</span>
                                            <span className="as-li-d">{quando(c.atualizadaEm)}</span>
                                        </button>
                                    )}

                                    <div className="as-li-acts">
                                        <button
                                            type="button"
                                            className="as-ic"
                                            onClick={() => { setRascunho(c.titulo); setRenomeando(c.conversaId); }}
                                            aria-label={`Renomear ${c.titulo}`}
                                            title="Renomear"
                                        >
                                            <LuPencil size={14} />
                                        </button>
                                        <button
                                            type="button"
                                            className="as-ic is-danger"
                                            onClick={() => setAExcluir(c)}
                                            aria-label={`Excluir ${c.titulo}`}
                                            title="Excluir"
                                        >
                                            <LuTrash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <ConfirmationModal
                isOpen={aExcluir !== null}
                onClose={() => setAExcluir(null)}
                onConfirm={excluir}
                title="Excluir conversa"
                message={`"${aExcluir?.titulo ?? ''}" sai da sua lista e não dá para recuperar. As telas e os dados da plataforma não mudam.`}
                confirmText="Excluir"
                isConfirming={excluindo}
            />
        </div>
    );
};

export default AssistenteConversasPage;
